import { el } from '../js/utils.js';
import { t, getLang, setLang } from '../js/i18n.js';
import { flagFR, flagGB } from './flags.js';

/**
 * Retourne un <header> global à insérer en premier dans chaque page.
 *
 * @param {{ variant?: 'standard' | 'over-dark' }} opts
 *   variant 'over-dark' → texte clair sur fond sombre (home)
 *   variant 'standard'  → texte sombre sur fond crème (autres pages)
 */
export function renderHeader({ variant = 'over-dark' } = {}) {
  const cls =
    variant === 'over-dark'
      ? 'app-header app-header--over-dark'
      : 'app-header app-header--standard';

  // --- Bouton hamburger (mobile) ---
  const hamburger = el('button', {
    class: 'app-header-hamburger',
    'aria-label': t('nav.menu.open'),
    'aria-expanded': 'false',
    'aria-controls': 'app-nav-panel',
  }, '☰');

  // --- Backdrop (clic pour fermer) ---
  const backdrop = el('div', {
    class: 'app-nav-backdrop',
    'aria-hidden': 'true',
    hidden: true,
  });

  // --- Panneau mobile ---
  const panel = el(
    'div',
    {
      class: 'app-nav-panel',
      id: 'app-nav-panel',
      role: 'dialog',
      'aria-label': t('nav.menu.open'),
      hidden: true,
    },
    el(
      'nav',
      { class: 'app-nav-links' },
      el('a', { href: '#/', class: 'app-nav-link' }, t('nav.home')),
      el('a', { href: '#/species', class: 'app-nav-link' }, t('nav.catalog')),
      el('a', { href: '#/swipe', class: 'app-nav-link' }, t('nav.survey')),
      el('a', { href: '#/help', class: 'app-nav-link' }, t('nav.help'))
    ),
    el('hr', { class: 'app-nav-sep' }),
    buildFlagSwitcher()
  );

  // --- Logique ouverture / fermeture ---
  function closeOnNav() {
    close();
  }

  function open() {
    panel.hidden = false;
    backdrop.hidden = false;
    hamburger.setAttribute('aria-expanded', 'true');
    hamburger.setAttribute('aria-label', t('nav.menu.close'));
    hamburger.textContent = '✕';
    window.addEventListener('hashchange', closeOnNav);
  }

  function close() {
    panel.hidden = true;
    backdrop.hidden = true;
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.setAttribute('aria-label', t('nav.menu.open'));
    hamburger.textContent = '☰';
    window.removeEventListener('hashchange', closeOnNav);
  }

  hamburger.addEventListener('click', () => {
    if (panel.hidden) open();
    else close();
  });

  backdrop.addEventListener('click', close);

  // Ferme le panneau au clic sur un lien (ex : navigation vers la page courante)
  panel.querySelectorAll('.app-nav-link').forEach((link) => {
    link.addEventListener('click', close);
  });

  // --- Structure ---
  return el(
    'header',
    { class: cls },
    el(
      'div',
      { class: 'app-header-inner container-wide' },
      el(
        'a',
        { href: '#/', class: 'app-header-logo', 'aria-label': 'Census by Doodive' },
        el('img', { src: '/icons/logo.svg', alt: '', class: 'app-header-logo-img' })
      ),
      el(
        'div',
        { class: 'app-header-brand', role: 'presentation' },
        el('span', { class: 'app-header-brand-title' },
          el('strong', {}, 'census'),
          '.doodive.com'
        ),
        el('span', { class: 'app-header-brand-rule', 'aria-hidden': 'true' }),
        el('span', { class: 'app-header-brand-tagline' },
          t('header.baseline.line1'),
          el('br', {}),
          t('header.baseline.line2')
        )
      ),
      el(
        'div',
        { class: 'app-header-right' },
        el(
          'nav',
          { class: 'app-header-desktop-nav', 'aria-label': 'Navigation' },
          buildFlagSwitcher(),
          el('a', { href: '#/help', class: 'app-header-help-link' }, t('nav.help'))
        ),
        hamburger
      )
    ),
    backdrop,
    panel
  );
}

function buildFlagSwitcher() {
  const lang = getLang();

  const frAttrs = { class: 'lang-btn', 'aria-label': 'Français', 'data-lang': 'fr', onclick: () => setLang('fr') };
  if (lang === 'fr') frAttrs['aria-current'] = 'true';
  const btnFR = el('button', frAttrs);
  btnFR.innerHTML = flagFR();

  const enAttrs = { class: 'lang-btn', 'aria-label': 'English', 'data-lang': 'en', onclick: () => setLang('en') };
  if (lang === 'en') enAttrs['aria-current'] = 'true';
  const btnEN = el('button', enAttrs);
  btnEN.innerHTML = flagGB();

  return el(
    'div',
    { class: 'lang-flag-switcher', role: 'group', 'aria-label': 'Language / Langue' },
    btnFR,
    btnEN
  );
}
